import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import '../styles/LiveRateTicker.css';

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000; // flag as stale after 24h

const fmt = (num) =>
    num != null ? Number(num).toLocaleString('en-IN') : '—';

const ageLabel = (date) => {
    if (!date) return '';
    const diffMs = Date.now() - new Date(date).getTime();
    const diffH = Math.floor(diffMs / (1000 * 60 * 60));
    if (diffH < 1) return 'just now';
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    return `${diffD}d ago`;
};

export const LiveRateTicker = () => {
    const [rates, setRates] = useState({ gold24K: null, gold22K: null, silver: null });
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isStale, setIsStale] = useState(false);

    const fetchRates = useCallback(async () => {
        try {
            const { data } = await axios.get('/api/rates');
            const gold24K = data.find(r => r.metalType === 'Gold 24K') || null;
            const gold22K = data.find(r => r.metalType === 'Gold 22K') || null;
            const silver  = data.find(r => r.metalType === 'Silver')   || null;

            setRates({ gold24K, gold22K, silver });

            if (data.length) {
                const newest = new Date(Math.max(...data.map(r => new Date(r.lastUpdated).getTime())));
                setLastUpdated(newest);
                setIsStale(Date.now() - newest.getTime() > STALE_THRESHOLD_MS);
            }
        } catch {
            // keep whatever was shown before
        }
    }, []);

    useEffect(() => {
        fetchRates();
        const id = setInterval(fetchRates, 30 * 60 * 1000); // re-check every 30 min
        return () => clearInterval(id);
    }, [fetchRates]);

    const today = new Date().toLocaleDateString('en-GB', {
        day: '2-digit', month: 'short', year: 'numeric'
    }).toUpperCase();

    // Build ticker items (only show if we have data)
    const hasData = rates.gold24K || rates.gold22K || rates.silver;

    return (
        <div className="live-rate-ticker-container">
            <div className="ticker-label">
                <span className={`live-dot${isStale ? ' stale' : ''}`}>●</span>
                {isStale ? 'RATES' : 'LIVE RATES'}
            </div>
            <div className="ticker-wrapper">
                <div className="ticker-content">
                    {[1, 2, 3].map((i) => (
                        <React.Fragment key={i}>
                            <div className="ticker-item highlight-date">
                                <span className="item-name">DATE</span>
                                <span className="item-price">{today}</span>
                            </div>

                            {rates.gold24K && (
                                <div className="ticker-item">
                                    <span className="item-name">GOLD 24K</span>
                                    <span className="item-price">₹{fmt(rates.gold24K.rate)}/10g</span>
                                </div>
                            )}

                            {rates.gold22K && (
                                <div className="ticker-item">
                                    <span className="item-name">GOLD 22K</span>
                                    <span className="item-price">₹{fmt(rates.gold22K.rate)}/10g</span>
                                </div>
                            )}

                            {rates.silver && (
                                <div className="ticker-item">
                                    <span className="item-name">SILVER</span>
                                    <span className="item-price">₹{fmt(rates.silver.rate)}/kg</span>
                                </div>
                            )}

                            {!hasData && (
                                <div className="ticker-item">
                                    <span className="item-name">GOLD 22K</span>
                                    <span className="item-price" style={{ color: '#666' }}>Loading…</span>
                                </div>
                            )}

                            {lastUpdated && (
                                <div className="ticker-item highlight-date">
                                    <span className="item-name">UPDATED</span>
                                    <span className="item-price">{ageLabel(lastUpdated)}</span>
                                </div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>
        </div>
    );
};
